import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    CardActions,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Snackbar,
    DialogContentText,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Preview as PreviewIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import { formTemplatesApi } from '../services/api';
import TableFieldEditor from '../components/TableFieldEditor';
import TableField from '../components/TableField';

const Forms = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    
    // New delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    
    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuTemplateId, setMenuTemplateId] = useState(null);
    
    // Form builder state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        form_schema: { fields: [] }
    });

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const response = await formTemplatesApi.getAll();
            setTemplates(response.data);
        } catch (err) {
            setError('Failed to load form templates');
            console.error('Error loading templates:', err);
        } finally {
            setLoading(false);
        }
    };

    const cleanFormDataForSave = (data) => {
        // Clean up empty options from select fields before saving
        const cleanedFields = data.form_schema.fields.map(field => {
            if (field.type === 'select' && field.options) {
                return { ...field, options: field.options.filter(opt => opt.trim()) };
            }
            return field;
        });
        return { ...data, form_schema: { ...data.form_schema, fields: cleanedFields } };
    };

    const handleCreateTemplate = async () => {
        try {
            if (!formData.name.trim()) {
                setError('Form name is required');
                return;
            }

            if (formData.form_schema.fields.length === 0) {
                setError('At least one form field is required');
                return;
            }

            await formTemplatesApi.create(cleanFormDataForSave(formData));
            setSuccess('Form template created successfully');
            setCreateDialogOpen(false);
            resetFormData();
            loadTemplates();
        } catch (err) {
            setError('Failed to create form template');
            console.error('Error creating template:', err);
        }
    };

    const handleUpdateTemplate = async () => {
        try {
            if (!formData.name.trim()) {
                setError('Form name is required');
                return;
            }

            await formTemplatesApi.update(selectedTemplate.template_id, cleanFormDataForSave(formData));
            setSuccess('Form template updated successfully');
            setEditDialogOpen(false);
            resetFormData();
            loadTemplates();
        } catch (err) {
            setError('Failed to update form template');
            console.error('Error updating template:', err);
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!templateId) return;
        try {
            await formTemplatesApi.delete(templateId);
            setSuccess('Form template deleted successfully');
            loadTemplates();
        } catch (err) {
            setError('Failed to delete form template');
            console.error('Error deleting template:', err);
        } finally {
            setDeleteDialogOpen(false);
            setTemplateToDelete(null);
        }
    };

    const openDeleteDialog = (templateId) => {
        setTemplateToDelete(templateId);
        setDeleteDialogOpen(true);
    };

    const resetFormData = () => {
        setFormData({
            name: '',
            description: '',
            form_schema: { fields: [] }
        });
        setSelectedTemplate(null);
    };

    const openEditDialog = (template) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name,
            description: template.description || '',
            form_schema: template.form_schema || { fields: [] }
        });
        setEditDialogOpen(true);
    };

    const openPreviewDialog = (template) => {
        setSelectedTemplate(template);
        setPreviewDialogOpen(true);
    };

    const handleMenuOpen = (event, templateId) => {
        setAnchorEl(event.currentTarget);
        setMenuTemplateId(templateId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuTemplateId(null);
    };

    const addFormField = (fieldType) => {
        const newField = {
            id: Date.now(),
            type: fieldType,
            label: `New ${fieldType} field`,
            required: false,
            options: fieldType === 'select' ? ['Option 1', 'Option 2'] : undefined,
            columns: fieldType === 'table' ? [] : undefined
        };

        setFormData(prev => ({
            ...prev,
            form_schema: {
                ...prev.form_schema,
                fields: [...prev.form_schema.fields, newField]
            }
        }));
    };

    const updateFormField = (fieldId, updates) => {
        setFormData(prev => ({
            ...prev,
            form_schema: {
                ...prev.form_schema,
                fields: prev.form_schema.fields.map(field =>
                    field.id === fieldId ? { ...field, ...updates } : field
                )
            }
        }));
    };

    const removeFormField = (fieldId) => {
        setFormData(prev => ({
            ...prev,
            form_schema: {
                ...prev.form_schema,
                fields: prev.form_schema.fields.filter(field => field.id !== fieldId)
            }
        }));
    };

    const duplicateTemplate = async (template) => {
        try {
            const duplicatedData = {
                name: `${template.name} (Copy)`,
                description: template.description,
                form_schema: template.form_schema
            };

            await formTemplatesApi.create(duplicatedData);
            setSuccess('Form template duplicated successfully');
            loadTemplates();
        } catch (err) {
            setError('Failed to duplicate form template');
            console.error('Error duplicating template:', err);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Form Templates
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Create New Form
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {templates.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No form templates yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Create your first form template to get started
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateDialogOpen(true)}
                        >
                            Create First Form
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {templates.map((template) => (
                        <Grid item xs={12} sm={6} md={4} key={template.template_id}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="h6" component="h2" gutterBottom>
                                                {template.name}
                                            </Typography>
                                            {template.description && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {template.description}
                                                </Typography>
                                            )}
                                            <Chip
                                                label={`${template.form_schema?.fields?.length || 0} fields`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, template.template_id)}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="small"
                                        startIcon={<PreviewIcon />}
                                        onClick={() => openPreviewDialog(template)}
                                    >
                                        Preview
                                    </Button>
                                    <Button
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => openEditDialog(template)}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => openDeleteDialog(template.template_id)}
                                    >
                                        Delete
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Template Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    const template = templates.find(t => t.template_id === menuTemplateId);
                    duplicateTemplate(template);
                    handleMenuClose();
                }}>
                    <CopyIcon sx={{ mr: 1 }} fontSize="small" />
                    Duplicate
                </MenuItem>
                <MenuItem onClick={() => {
                    openDeleteDialog(menuTemplateId);
                    handleMenuClose();
                }}>
                    <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                    Delete
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Form Template</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this form template? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={() => handleDeleteTemplate(templateToDelete)}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Create Form Dialog */}
            <FormBuilderDialog
                open={createDialogOpen}
                onClose={() => {
                    setCreateDialogOpen(false);
                    resetFormData();
                }}
                onSave={handleCreateTemplate}
                formData={formData}
                setFormData={setFormData}
                title="Create New Form Template"
                addFormField={addFormField}
                updateFormField={updateFormField}
                removeFormField={removeFormField}
            />

            {/* Edit Form Dialog */}
            <FormBuilderDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    resetFormData();
                }}
                onSave={handleUpdateTemplate}
                formData={formData}
                setFormData={setFormData}
                title="Edit Form Template"
                addFormField={addFormField}
                updateFormField={updateFormField}
                removeFormField={removeFormField}
            />

            {/* Preview Form Dialog */}
            <FormPreviewDialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                template={selectedTemplate}
            />

            {/* Success Snackbar */}
            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess('')}
                message={success}
            />
        </Container>
    );
};

// Form Builder Dialog Component
const FormBuilderDialog = ({ 
    open, 
    onClose, 
    onSave, 
    formData, 
    setFormData, 
    title,
    addFormField,
    updateFormField,
    removeFormField
}) => {
    const fieldTypes = [
        { value: 'text', label: 'Text Input' },
        { value: 'textarea', label: 'Text Area' },
        { value: 'select', label: 'Dropdown' },
        { value: 'checkbox', label: 'Checkbox' },
        { value: 'signature', label: 'Signature' },
        { value: 'date', label: 'Date' },
        { value: 'number', label: 'Number' },
        { value: 'photo', label: 'Photo Upload' },
        { value: 'pdf', label: 'PDF Upload' },
        { value: 'table', label: 'Table Input' }
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Form Name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Description (optional)"
                        multiline
                        rows={2}
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    />
                </Box>

                <Typography variant="h6" gutterBottom>
                    Form Fields
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Add fields to your form:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {fieldTypes.map((type) => (
                            <Button
                                key={type.value}
                                variant="outlined"
                                size="small"
                                onClick={() => addFormField(type.value)}
                            >
                                + {type.label}
                            </Button>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {formData.form_schema.fields.map((field, index) => (
                        <FormFieldEditor
                            key={field.id}
                            field={field}
                            index={index}
                            onUpdate={(updates) => updateFormField(field.id, updates)}
                            onRemove={() => removeFormField(field.id)}
                        />
                    ))}
                    
                    {formData.form_schema.fields.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                            No fields added yet. Use the buttons above to add form fields.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={onSave} variant="contained">
                    Save Form Template
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Form Field Editor Component
const FormFieldEditor = ({ field, index, onUpdate, onRemove }) => {
    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle2" color="primary">
                        Field {index + 1}: {field.type}
                    </Typography>
                    <IconButton size="small" onClick={onRemove}>
                        <DeleteIcon />
                    </IconButton>
                </Box>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            label="Field Label"
                            value={field.label}
                            onChange={(e) => onUpdate({ label: e.target.value })}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Required</InputLabel>
                            <Select
                                value={field.required ? 'yes' : 'no'}
                                onChange={(e) => onUpdate({ required: e.target.value === 'yes' })}
                                label="Required"
                            >
                                <MenuItem value="no">No</MenuItem>
                                <MenuItem value="yes">Yes</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    {field.type === 'select' && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Options (one per line)"
                                multiline
                                rows={3}
                                value={field.options?.join('\n') || ''}
                                onChange={(e) => onUpdate({ options: e.target.value.split('\n') })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.stopPropagation();
                                    }
                                }}
                                size="small"
                                helperText="Enter each option on a new line"
                            />
                        </Grid>
                    )}
                    
                    {field.type === 'table' && (
                        <Grid item xs={12}>
                            <TableFieldEditor
                                field={field}
                                onUpdate={onUpdate}
                            />
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};

// Form Preview Dialog Component
const FormPreviewDialog = ({ open, onClose, template }) => {
    if (!template) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Preview: {template.name}</DialogTitle>
            <DialogContent>
                {template.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {template.description}
                    </Typography>
                )}
                
                <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                    {template.form_schema?.fields?.map((field, index) => (
                        <Box key={index} sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
                            </Typography>
                            
                            {field.type === 'text' && (
                                <TextField fullWidth disabled placeholder="Text input field" />
                            )}
                            
                            {field.type === 'textarea' && (
                                <TextField fullWidth multiline rows={3} disabled placeholder="Text area field" />
                            )}
                            
                            {field.type === 'select' && (
                                <FormControl fullWidth disabled>
                                    <Select value="">
                                        {field.options?.map((option, i) => (
                                            <MenuItem key={i} value={option}>{option}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            
                            {field.type === 'checkbox' && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <input type="checkbox" disabled />
                                    <Typography variant="body2" sx={{ ml: 1 }}>Checkbox option</Typography>
                                </Box>
                            )}
                            
                            {field.type === 'signature' && (
                                <Box sx={{ 
                                    border: '2px dashed', 
                                    borderColor: 'divider',
                                    height: 150,
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    bgcolor: 'grey.50'
                                }}>
                                    <Typography color="text.secondary">Signature Area</Typography>
                                </Box>
                            )}
                            
                            {field.type === 'date' && (
                                <TextField type="date" fullWidth disabled />
                            )}
                            
                            {field.type === 'number' && (
                                <TextField type="number" fullWidth disabled placeholder="Number input field" />
                            )}
                            
                            {field.type === 'photo' && (
                                <Box sx={{ 
                                    border: '2px dashed', 
                                    borderColor: 'divider',
                                    height: 120,
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    bgcolor: 'grey.50',
                                    borderRadius: 1
                                }}>
                                    <Typography color="text.secondary">Photo Upload Area</Typography>
                                </Box>
                            )}
                            
                            {field.type === 'pdf' && (
                                <Box sx={{ 
                                    border: '2px dashed', 
                                    borderColor: 'divider',
                                    p: 2,
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    bgcolor: 'grey.50',
                                    borderRadius: 1
                                }}>
                                    <Typography color="text.secondary">PDF Upload Area</Typography>
                                </Box>
                            )}
                            
                            {field.type === 'table' && (
                                <Box>
                                    <TableField
                                        field={field}
                                        value={[]}
                                        onChange={() => {}}
                                        disabled={true}
                                        isEditing={false}
                                    />
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default Forms;
